import { ENEMIES } from "../config/enemies";
import { BALANCE } from "../config/balance";
import { DIFFICULTIES } from "../config/campaign";
import { CASTLE_APPROACH, MAPS } from "../config/map";
import { TOWERS, towerStatsForInstance } from "../config/towers";
import { buildWaveSet } from "../config/waves";
import { TOWER_ABILITIES } from "../config/abilities";
import type {
  CampaignScenarioDefinition,
  DifficultyDefinition,
  EnemyInstance,
  GridCoordinate,
  LevelResult,
  MapDefinition,
  TargetingMode,
  TowerInstance,
  TowerUpgradeDefinition,
  DraftAbilityDefinition,
} from "../domain/types";
import { defenseAnalytics } from "../systems/analytics";
import { CombatManager } from "../systems/combat";
import { EconomyManager } from "../systems/economy";
import { GridManager } from "../systems/grid";
import { NavigationManager } from "../systems/navigation";
import { WaveManager } from "../systems/waves";
import {
  advancePresentation,
  beginDying,
  beginEscape,
  isMoving,
  isTargetable,
} from "../systems/enemy-lifecycle";
import { events } from "./events";

const MODES: readonly TargetingMode[] = ["first", "last", "strongest", "weakest", "closest"];

export class DefenseGameState {
  readonly map: MapDefinition;
  readonly grid: GridManager;
  readonly navigation: NavigationManager;
  readonly economy: EconomyManager;
  readonly combat = new CombatManager();
  readonly difficulty: DifficultyDefinition;
  readonly waves: WaveManager;
  readonly analytics = defenseAnalytics;
  readonly towers = new Map<string, TowerInstance>();
  readonly enemies = new Map<string, EnemyInstance>();
  readonly loadedTowerIds: readonly string[];
  readonly towerAbilityStacks = new Map<string, number>();
  pendingDraftChoices: readonly DraftAbilityDefinition[] = [];
  lives: number;
  score = 0;
  speed = 1;
  paused = false;
  ended = false;
  victory = false;
  selectedBuild: string | null = null;
  selectedTower: string | null = null;
  preparationRemainingMs: number = BALANCE.wavePreparationMs;
  private towerSequence = 0;
  private enemySequence = 0;
  private pausedForDraft = false;
  private debugInfiniteMoneyEnabled = false;
  private debugInfiniteHealthEnabled = false;
  private debugAwardSequence = 0;
  private debugEnemyMovementEnabled = true;
  private debugTowerAttacksEnabled = true;
  private readonly scenario: CampaignScenarioDefinition | undefined;
  private readonly startingLives: number;

  constructor(
    map: MapDefinition = CASTLE_APPROACH,
    scenario?: CampaignScenarioDefinition,
    loadedTowerIds: readonly string[] = Object.keys(TOWERS).slice(0, 5),
  ) {
    this.map = map;
    this.scenario = scenario;
    this.loadedTowerIds = loadedTowerIds.filter((id) => Boolean(TOWERS[id])).slice(0, 5);
    this.difficulty = DIFFICULTIES[scenario?.difficulty ?? "easy"];
    this.waves = new WaveManager(buildWaveSet(this.difficulty.waveCount));
    this.grid = new GridManager(this.map);
    this.navigation = new NavigationManager(this.grid);
    this.economy = new EconomyManager(
      scenario?.startingCurrency ??
        Math.round(this.map.startingCurrency * this.difficulty.startingCurrencyMultiplier),
    );
    this.lives =
      scenario?.startingLives ??
      Math.max(8, Math.round(this.map.startingLives * this.difficulty.livesMultiplier));
    this.startingLives = this.lives;
  }

  selectBuild(definitionId: string | null): void {
    const nextDefinitionId =
      definitionId !== null && this.selectedBuild === definitionId ? null : definitionId;
    this.selectedBuild = nextDefinitionId;
    this.selectedTower = null;
    events.emit("build:selected", { towerDefinitionId: nextDefinitionId });
    events.emit("tower:selected", { towerId: null });
  }
  selectTower(id: string | null): void {
    this.selectedTower = id;
    this.selectedBuild = null;
    events.emit("tower:selected", { towerId: id });
    events.emit("build:selected", { towerDefinitionId: null });
  }

  setPaused(paused: boolean): void {
    if (this.ended || this.pausedForDraft || this.paused === paused) return;
    this.paused = paused;
    events.emit("state:changed", undefined);
  }

  togglePaused(): void {
    this.setPaused(!this.paused);
  }

  cycleSpeed(): void {
    if (this.ended) return;
    this.speed = this.speed >= 3 ? 1 : this.speed + 1;
    events.emit("state:changed", undefined);
  }

  placeTower(definitionId: string, origin: GridCoordinate): boolean {
    const definition = TOWERS[definitionId];
    if (!definition) return false;
    const basic = this.grid.basicPlacementCheck(origin, definition.footprint);
    const cells = basic.cells;
    if (!basic.valid) return this.reject(basic.reason);
    if (!this.economy.canAfford(definition.cost))
      return this.reject("The treasury cannot afford that tower.");
    const simulated = this.navigation.simulatePlacement(cells, [...this.enemies.values()]);
    if (!simulated.valid) return this.reject(simulated.reason);
    const id = `tower-${++this.towerSequence}`;
    this.economy.spend(`build-${id}`, "tower-purchase", definition.cost, definition.displayName);
    this.navigation.commitPlacement(id, cells, simulated.navigation);
    const tower: TowerInstance = {
      id,
      definitionId,
      origin: { ...origin },
      level: 1,
      targetingMode: "first",
      invested: definition.cost,
      cooldownMs: 180,
      abilityStacks: this.towerAbilityStacks.get(definitionId) ?? 0,
    };
    this.applyAbilityModifiers(tower);
    this.towers.set(id, tower);
    events.emit("navigation:changed", { revision: this.navigation.snapshot.revision });
    events.emit("state:changed", undefined);
    this.analytics.track("tower_placed", { tower: definitionId });
    events.emit("tower:placed", { towerId: id });
    return true;
  }

  placeTowerAuthoritative(definitionId: string, origin: GridCoordinate): string | null {
    const definition = TOWERS[definitionId];
    if (!definition) return null;
    const basic = this.grid.basicPlacementCheck(origin, definition.footprint);
    if (!basic.valid) return null;
    const simulated = this.navigation.simulatePlacement(basic.cells, [...this.enemies.values()]);
    if (!simulated.valid) return null;
    const id = `tower-${++this.towerSequence}`;
    this.navigation.commitPlacement(id, basic.cells, simulated.navigation);
    const tower: TowerInstance = {
      id,
      definitionId,
      origin: { ...origin },
      level: 1,
      targetingMode: "first",
      invested: definition.cost,
      cooldownMs: 180,
      abilityStacks: this.towerAbilityStacks.get(definitionId) ?? 0,
    };
    this.applyAbilityModifiers(tower);
    this.towers.set(id, tower);
    events.emit("navigation:changed", { revision: this.navigation.snapshot.revision });
    events.emit("tower:placed", { towerId: id });
    events.emit("state:changed", undefined);
    return id;
  }

  removeTowerAuthoritative(id: string): void {
    if (!this.towers.has(id)) return;
    this.towers.delete(id);
    this.navigation.releaseTower(id);
    events.emit("tower:sold", { towerId: id });
    events.emit("navigation:changed", { revision: this.navigation.snapshot.revision });
    events.emit("state:changed", undefined);
  }

  syncAuthoritativeSummary(gold: number, lives: number, score: number, wave: number): void {
    this.economy.setAuthoritativeBalance(gold);
    this.lives = Math.max(0, Math.floor(lives));
    this.score = Math.max(0, Math.floor(score));
    while (this.waves.currentWaveNumber < wave && this.waves.nextWave) this.startWave(false);
    events.emit("state:changed", undefined);
  }

  previewPlacement(
    definitionId: string,
    origin: GridCoordinate,
  ): import("../domain/types").PlacementResult {
    const definition = TOWERS[definitionId];
    if (!definition) return { valid: false, reason: "Unknown tower.", cells: [] };
    const basic = this.grid.basicPlacementCheck(origin, definition.footprint);
    if (!basic.valid) return basic;
    if (!this.economy.canAfford(definition.cost))
      return { valid: false, reason: "Not enough gold.", cells: basic.cells };
    return this.navigation.simulatePlacement(basic.cells, [...this.enemies.values()]);
  }

  upgradeTower(id: string, branchChoice?: "a" | "b"): boolean {
    const tower = this.towers.get(id),
      definition = tower ? TOWERS[tower.definitionId] : undefined;
    if (!tower || !definition) return false;

    const nextLevel = tower.level + 1;
    const candidates = definition.upgrades.filter((entry) => entry.level === nextLevel);
    if (candidates.length === 0) return this.reject("This tower is already at maximum rank.");

    let upgrade: TowerUpgradeDefinition | undefined;
    if (tower.branch) {
      upgrade = candidates.find(
        (entry) => entry.branch === "shared" || entry.branch === tower.branch,
      );
    } else if (candidates.some((entry) => entry.branch === "a" || entry.branch === "b")) {
      if (!branchChoice) return this.reject("Choose an evolution path for this tower.");
      upgrade = candidates.find((entry) => entry.branch === branchChoice);
    } else {
      upgrade = candidates.find((entry) => entry.branch === "shared" || !entry.branch);
    }

    if (!upgrade) return this.reject("That evolution is not available for this tower.");
    if (
      !this.economy.spend(
        `upgrade-${id}-${upgrade.level}-${upgrade.branch ?? "shared"}`,
        "tower-upgrade",
        upgrade.cost,
        `${definition.displayName}: ${upgrade.name}`,
      )
    )
      return this.reject("The treasury cannot afford that upgrade.");

    if (!tower.branch && (upgrade.branch === "a" || upgrade.branch === "b")) {
      tower.branch = upgrade.branch;
    }
    tower.level = upgrade.level;
    tower.invested += upgrade.cost;
    events.emit("tower:upgraded", { towerId: id, level: tower.level });
    events.emit("state:changed", undefined);
    return true;
  }

  sellTower(id: string): boolean {
    const tower = this.towers.get(id),
      definition = tower ? TOWERS[tower.definitionId] : undefined;
    if (!tower || !definition) return false;
    this.economy.award(
      `sell-${id}`,
      "tower-sale",
      this.economy.sellRefund(tower.invested, definition.sellRefundRate),
      definition.displayName,
    );
    this.towers.delete(id);
    this.navigation.releaseTower(id);
    this.selectedTower = null;
    events.emit("tower:sold", { towerId: id });
    events.emit("navigation:changed", { revision: this.navigation.snapshot.revision });
    events.emit("state:changed", undefined);
    return true;
  }

  cycleTargeting(id: string): void {
    const tower = this.towers.get(id);
    if (!tower) return;
    tower.targetingMode = MODES[(MODES.indexOf(tower.targetingMode) + 1) % MODES.length]!;
    events.emit("state:changed", undefined);
  }
  startWave(manual = true): boolean {
    const next = this.waves.nextWave;
    const started = this.waves.startNextWave();
    if (started) {
      if (manual && next && this.preparationRemainingMs > 0) {
        this.economy.award(
          `early-wave-${next.number}`,
          "early-wave-reward",
          this.earlyWaveReward,
          next.name,
        );
      }
      this.preparationRemainingMs = 0;
      events.emit("wave:started", { wave: this.waves.currentWaveNumber });
      events.emit("state:changed", undefined);
    }
    return started;
  }

  update(deltaMs: number): void {
    if (this.debugInfiniteMoneyEnabled && this.economy.balance < 500000) {
      this.debugAwardGold(1000000);
    }
    if (this.paused || this.ended) return;
    if (!this.waves.running && this.waves.nextWave) {
      this.preparationRemainingMs = Math.max(0, this.preparationRemainingMs - deltaMs);
      if (this.preparationRemainingMs === 0) this.startWave(false);
    }
    const tick = this.waves.tick(deltaMs);
    for (const enemyId of tick.spawnEnemyIds) if (!this.spawn(enemyId)) this.waves.resolveEnemy();
    this.advanceEnemyPresentations(deltaMs);
    if (this.debugEnemyMovementEnabled) this.moveEnemies(deltaMs);
    if (this.debugTowerAttacksEnabled) this.updateCombat(deltaMs);
    if (tick.waveCompleted) {
      this.economy.award(
        `wave-${this.waves.currentWaveNumber}`,
        "wave-reward",
        this.waves.claimCompletionReward(),
        `Wave ${this.waves.currentWaveNumber}`,
      );
      for (const wave of tick.completedWaveNumbers) {
        events.emit("wave:completed", { wave });
        if (
          wave % 5 === 0 &&
          wave < this.waves.totalWaves &&
          this.pendingDraftChoices.length === 0
        ) {
          this.offerDraft();
        }
      }
      if (tick.victory) this.end(true);
      else this.preparationRemainingMs = BALANCE.wavePreparationMs;
    }
    events.emit("state:changed", undefined);
  }

  chooseDraftAbility(abilityId: string): boolean {
    const ability = this.pendingDraftChoices.find((choice) => choice.id === abilityId);
    if (!ability) return false;
    const stacks = (this.towerAbilityStacks.get(ability.towerId) ?? 0) + 1;
    this.towerAbilityStacks.set(ability.towerId, stacks);
    for (const tower of this.towers.values()) {
      if (tower.definitionId === ability.towerId) this.applyAbilityModifiers(tower);
    }
    this.pendingDraftChoices = [];
    this.pausedForDraft = false;
    this.paused = false;
    events.emit("draft:selected", { abilityId: ability.id, towerId: ability.towerId });
    events.emit("state:changed", undefined);
    return true;
  }

  private offerDraft(): void {
    const pool = this.loadedTowerIds
      .map((towerId) => TOWER_ABILITIES[towerId])
      .filter((ability): ability is DraftAbilityDefinition => Boolean(ability));
    if (pool.length === 0) return;
    const rotated = [...pool].sort((a, b) => {
      const aStacks = this.towerAbilityStacks.get(a.towerId) ?? 0;
      const bStacks = this.towerAbilityStacks.get(b.towerId) ?? 0;
      return aStacks - bStacks || a.id.localeCompare(b.id);
    });
    this.pendingDraftChoices = rotated.slice(0, Math.min(3, rotated.length));
    this.pausedForDraft = true;
    this.paused = true;
    events.emit("draft:offered", {
      abilityIds: this.pendingDraftChoices.map((choice) => choice.id),
    });
  }

  private applyAbilityModifiers(tower: TowerInstance): void {
    const ability = TOWER_ABILITIES[tower.definitionId];
    const stacks = this.towerAbilityStacks.get(tower.definitionId) ?? 0;
    tower.abilityStacks = stacks;
    tower.damageMultiplier = ability?.effects.damageMultiplier
      ? ability.effects.damageMultiplier ** stacks
      : 1;
    tower.intervalMultiplier = ability?.effects.intervalMultiplier
      ? ability.effects.intervalMultiplier ** stacks
      : 1;
    tower.rangeBonus = (ability?.effects.rangeBonus ?? 0) * stacks;
    tower.splashBonus = (ability?.effects.splashBonus ?? 0) * stacks;
    tower.targetBonus = (ability?.effects.targetBonus ?? 0) * stacks;
    tower.armorBreakBonus = (ability?.effects.armorBreakBonus ?? 0) * stacks;
    tower.revealAuraBonus = (ability?.effects.revealAuraBonus ?? 0) * stacks;
  }

  result(): LevelResult {
    return {
      mapId: this.map.id,
      scenarioId: this.scenario?.id ?? `${this.map.id}:${this.difficulty.id}`,
      difficulty: this.difficulty.id,
      startingLives: this.startingLives,
      victory: this.victory,
      wavesCompleted: this.waves.currentWaveNumber,
      livesRemaining: this.lives,
      score: this.score,
      completedAt: new Date().toISOString(),
    };
  }
  get earlyWaveReward(): number {
    return Math.floor(this.preparationRemainingMs / 1000) * BALANCE.earlyWaveRewardPerSecond;
  }
  stressSpawn(count = 150): void {
    for (let index = 0; index < count; index += 1)
      this.spawn(index % 7 === 0 ? "scout" : "footman");
  }

  private spawn(definitionId: string): boolean {
    const definition = ENEMIES[definitionId],
      entrance = this.map.entrances[0];
    if (!definition || !entrance) return false;
    const id = `enemy-${++this.enemySequence}`,
      cell = { ...entrance.cell },
      mapIndex = Math.max(0, Object.keys(MAPS).indexOf(this.map.id)),
      waveIndex = Math.max(0, this.waves.currentWaveNumber - 1),
      healthMultiplier =
        BALANCE.baseEnemyHealthMultiplier *
        (1 + mapIndex * BALANCE.enemyHealthPerMap) *
        (1 + waveIndex * BALANCE.enemyHealthPerWave) *
        this.difficulty.enemyHealthMultiplier,
      maximumHealth = Math.round(definition.maximumHealth * healthMultiplier);
    this.enemies.set(id, {
      id,
      definitionId,
      health: maximumHealth,
      maximumHealth,
      effectiveArmor: definition.armor,
      cell,
      position: { x: cell.x - 0.7, y: cell.y + 0.5 },
      targetCell: null,
      navigationRevision: this.navigation.snapshot.revision,
      state: "pending",
      lifecycleElapsedMs: 0,
      lifecycleRemainingMs: 0,
      resolved: false,
      lastDamageType: null,
      distanceToExit: this.navigation.distanceAt(cell),
      statuses: [],
      shieldHealth: Math.round((definition.shield ?? 0) * healthMultiplier),
      maximumShield: Math.round((definition.shield ?? 0) * healthMultiplier),
      bossStageIndex: 0,
    });
    events.emit("enemy:spawned", { enemyId: id, durationMs: definition.spawnDurationMs });
    return true;
  }

  private advanceEnemyPresentations(deltaMs: number): void {
    const entrance = this.map.entrances[0];
    for (const enemy of [...this.enemies.values()]) {
      const definition = ENEMIES[enemy.definitionId];
      if (!definition) continue;
      const previous = enemy.state;
      advancePresentation(enemy, definition, deltaMs);
      if (enemy.state === "spawning" && entrance) {
        const progress = Math.min(1, enemy.lifecycleElapsedMs / definition.spawnDurationMs),
          eased = 1 - (1 - progress) ** 3;
        enemy.position.x = entrance.cell.x - 0.7 + 1.2 * eased;
        enemy.position.y = entrance.cell.y + 0.5;
      }
      if (previous === "spawning" && enemy.state === "active" && entrance) {
        enemy.cell = { ...entrance.cell };
        enemy.position = { x: entrance.cell.x + 0.5, y: entrance.cell.y + 0.5 };
        enemy.targetCell = this.navigation.nextCell(enemy.cell);
        enemy.navigationRevision = this.navigation.snapshot.revision;
        events.emit("enemy:activated", { enemyId: enemy.id });
      }
      if (enemy.state === "removed") {
        this.enemies.delete(enemy.id);
        events.emit("enemy:removed", { enemyId: enemy.id });
      }
    }
  }

  private moveEnemies(deltaMs: number): void {
    for (const enemy of this.enemies.values()) {
      if (!isMoving(enemy)) continue;
      const definition = ENEMIES[enemy.definitionId];
      if (!definition) continue;
      this.combat.updateStatuses(enemy, deltaMs);
      enemy.effectiveArmor = Math.min(
        0.85,
        enemy.effectiveArmor + this.auraMagnitudeAt(enemy, "armor"),
      );
      if (enemy.navigationRevision !== this.navigation.snapshot.revision) {
        const targetRemainsSafe =
          enemy.targetCell &&
          this.grid.isWalkable(enemy.targetCell) &&
          Number.isFinite(this.navigation.distanceAt(enemy.targetCell));
        if (!targetRemainsSafe) enemy.targetCell = this.navigation.nextCell(enemy.cell);
        enemy.navigationRevision = this.navigation.snapshot.revision;
      }
      this.applyEnemyAbilities(enemy, definition, deltaMs);
      const speedAura = this.auraMagnitudeAt(enemy, "speed");
      const berserkerMultiplier =
        definition.tags.includes("threshold-speed") &&
        enemy.health <= (enemy.maximumHealth ?? definition.maximumHealth) * 0.5
          ? 1.45
          : 1;
      const stageMultiplier = this.currentBossSpeedMultiplier(enemy, definition);
      let remaining =
        (definition.movementSpeed *
          this.difficulty.enemySpeedMultiplier *
          (1 + speedAura) *
          berserkerMultiplier *
          stageMultiplier *
          deltaMs) /
        1000;
      while (remaining > 0 && isMoving(enemy)) {
        enemy.targetCell ??= this.navigation.nextCell(enemy.cell);
        if (!enemy.targetCell) {
          this.escape(enemy);
          break;
        }
        const targetX = enemy.targetCell.x + 0.5,
          targetY = enemy.targetCell.y + 0.5,
          dx = targetX - enemy.position.x,
          dy = targetY - enemy.position.y,
          distance = Math.hypot(dx, dy);
        if (distance <= remaining + 0.0001) {
          enemy.position.x = targetX;
          enemy.position.y = targetY;
          remaining -= distance;
          enemy.cell = { ...enemy.targetCell };
          if (this.grid.cell(enemy.cell)?.isExit) {
            this.escape(enemy);
            break;
          }
          enemy.navigationRevision = this.navigation.snapshot.revision;
          enemy.targetCell = this.navigation.nextCell(enemy.cell);
        } else {
          enemy.position.x += (dx / distance) * remaining;
          enemy.position.y += (dy / distance) * remaining;
          remaining = 0;
        }
      }
      enemy.distanceToExit = this.navigation.distanceAt(enemy.cell);
    }
  }

  private updateCombat(deltaMs: number): void {
    const active = [...this.enemies.values()].filter(isTargetable);
    for (const tower of this.towers.values()) {
      tower.cooldownMs -= deltaMs;
      if (tower.cooldownMs > 0) continue;
      const definition = TOWERS[tower.definitionId];
      if (!definition) continue;
      const hits = this.combat.attack(tower, active, [...this.towers.values()]);
      if (hits.length === 0) continue;
      tower.cooldownMs += towerStatsForInstance(definition, tower).attack.intervalMs;
      events.emit("combat:fired", {
        towerId: tower.id,
        towerDefinitionId: tower.definitionId,
        enemyIds: hits.map((hit) => hit.enemyId),
      });
      for (const hit of hits) {
        if (hit.damage > 0)
          events.emit("enemy:hit", { enemyId: hit.enemyId, amount: hit.damage, type: hit.type });
        if (hit.killed) this.resolveEnemyDeath(hit.enemyId, hit.type);
      }
    }
  }

  resolveEnemyDeath(id: string, type: import("../domain/types").DamageType): void {
    const enemy = this.enemies.get(id),
      definition = enemy ? ENEMIES[enemy.definitionId] : undefined;
    if (!enemy || !definition || enemy.resolved || enemy.state !== "dying") return;
    enemy.resolved = true;
    this.waves.resolveEnemy();
    this.economy.award(
      `kill-${id}`,
      "enemy-reward",
      definition.rewardValue,
      definition.displayName,
    );
    this.score += definition.rewardValue * 10;
    if (definition.splitInto) {
      for (const split of definition.splitInto) {
        for (let index = 0; index < split.count; index += 1) {
          if (this.spawnAt(split.enemyId, enemy.cell, enemy.position)) this.waves.registerEnemy();
        }
      }
    }
    events.emit("enemy:destroyed", {
      enemyId: id,
      reward: definition.rewardValue,
      type,
      durationMs: definition.deathDurationMs,
    });
  }
  private spawnAt(
    definitionId: string,
    cell: GridCoordinate,
    position: { x: number; y: number },
  ): boolean {
    const definition = ENEMIES[definitionId];
    if (!definition) return false;
    const id = `enemy-${++this.enemySequence}`;
    const mapIndex = Math.max(0, Object.keys(MAPS).indexOf(this.map.id));
    const waveIndex = Math.max(0, this.waves.currentWaveNumber - 1);
    const healthMultiplier =
      BALANCE.baseEnemyHealthMultiplier *
      (1 + mapIndex * BALANCE.enemyHealthPerMap) *
      (1 + waveIndex * BALANCE.enemyHealthPerWave);
    const maximumHealth = Math.round(definition.maximumHealth * healthMultiplier);
    this.enemies.set(id, {
      id,
      definitionId,
      health: maximumHealth,
      maximumHealth,
      effectiveArmor: definition.armor,
      cell: { ...cell },
      position: { ...position },
      targetCell: this.navigation.nextCell(cell),
      navigationRevision: this.navigation.snapshot.revision,
      state: "active",
      lifecycleElapsedMs: 0,
      lifecycleRemainingMs: 0,
      resolved: false,
      lastDamageType: null,
      distanceToExit: this.navigation.distanceAt(cell),
      statuses: [],
      shieldHealth: Math.round((definition.shield ?? 0) * healthMultiplier),
      maximumShield: Math.round((definition.shield ?? 0) * healthMultiplier),
      bossStageIndex: 0,
    });
    events.emit("enemy:activated", { enemyId: id });
    return true;
  }

  private auraMagnitudeAt(enemy: EnemyInstance, kind: "speed" | "armor"): number {
    let magnitude = 0;
    for (const source of this.enemies.values()) {
      if (source.id === enemy.id || !isMoving(source)) continue;
      const sourceDefinition = ENEMIES[source.definitionId];
      const aura = sourceDefinition?.aura;
      if (!aura || aura.kind !== kind) continue;
      if (
        Math.hypot(source.position.x - enemy.position.x, source.position.y - enemy.position.y) <=
        aura.radius
      )
        magnitude = Math.max(magnitude, aura.magnitude);
    }
    return magnitude;
  }

  private applyEnemyAbilities(
    enemy: EnemyInstance,
    definition: import("../domain/types").EnemyDefinition,
    deltaMs: number,
  ): void {
    if (definition.regenerationPerSecond && enemy.health > 0) {
      enemy.health = Math.min(
        enemy.maximumHealth ?? definition.maximumHealth,
        enemy.health + (definition.regenerationPerSecond * deltaMs) / 1000,
      );
    }
    const stages = definition.bossStages ?? [];
    let index = enemy.bossStageIndex ?? 0;
    while (index < stages.length) {
      const stage = stages[index]!;
      const ratio = enemy.health / Math.max(1, enemy.maximumHealth ?? definition.maximumHealth);
      if (ratio > stage.healthRatio) break;
      index += 1;
      enemy.bossStageIndex = index;
      if (stage.shield) {
        enemy.shieldHealth = (enemy.shieldHealth ?? 0) + stage.shield;
        enemy.maximumShield = Math.max(enemy.maximumShield ?? 0, enemy.shieldHealth);
      }
    }
  }

  private currentBossSpeedMultiplier(
    enemy: EnemyInstance,
    definition: import("../domain/types").EnemyDefinition,
  ): number {
    const index = enemy.bossStageIndex ?? 0;
    if (index <= 0) return 1;
    return definition.bossStages?.[index - 1]?.speedMultiplier ?? 1;
  }

  private escape(enemy: EnemyInstance): void {
    const definition = ENEMIES[enemy.definitionId];
    if (!definition || enemy.resolved || !beginEscape(enemy, definition)) return;
    enemy.resolved = true;
    this.waves.resolveEnemy();
    const loss = this.debugInfiniteHealthEnabled ? 0 : definition.escapeValue;
    this.lives = Math.max(0, this.lives - loss);
    events.emit("enemy:escaped", {
      enemyId: enemy.id,
      livesLost: loss,
      durationMs: definition.escapeDurationMs,
    });
    if (this.lives === 0) this.end(false);
  }
  private end(victory: boolean): void {
    if (this.ended) return;
    this.ended = true;
    this.paused = true;
    this.victory = victory;
    events.emit("game:ended", { victory });
    this.analytics.track("game_ended", {
      victory,
      score: this.score,
      wave: this.waves.currentWaveNumber,
    });
  }

  get debugInfiniteMoney(): boolean {
    return this.debugInfiniteMoneyEnabled;
  }

  get debugInfiniteHealth(): boolean {
    return this.debugInfiniteHealthEnabled;
  }

  debugSetInfiniteMoney(enabled: boolean): void {
    this.debugInfiniteMoneyEnabled = enabled;
    if (enabled) this.debugAwardGold(1000000);
    events.emit("state:changed", undefined);
  }

  debugSetInfiniteHealth(enabled: boolean): void {
    this.debugInfiniteHealthEnabled = enabled;
    if (enabled) this.debugHealCastle();
    events.emit("state:changed", undefined);
  }

  debugAwardGold(amount = 100000): void {
    this.debugAwardSequence += 1;
    this.economy.award(
      `debug-gold-${this.debugAwardSequence}`,
      "wave-reward",
      Math.max(0, Math.floor(amount)),
      "Administrator debug grant",
    );
    events.emit("state:changed", undefined);
  }

  debugHealCastle(): void {
    this.lives = Math.max(this.lives, this.startingLives);
    events.emit("state:changed", undefined);
  }

  debugClearEnemies(): void {
    for (const enemy of this.enemies.values()) {
      const definition = ENEMIES[enemy.definitionId];
      if (!definition || enemy.resolved || !beginDying(enemy, definition)) continue;
      enemy.health = 0;
      this.resolveEnemyDeath(enemy.id, "physical");
    }
    events.emit("state:changed", undefined);
  }

  debugWinBattle(): void {
    this.end(true);
  }

  debugLoseBattle(): void {
    this.lives = 0;
    this.end(false);
  }

  get debugEnemyMovement(): boolean {
    return this.debugEnemyMovementEnabled;
  }

  get debugTowerAttacks(): boolean {
    return this.debugTowerAttacksEnabled;
  }

  debugSetEnemyMovement(enabled: boolean): void {
    this.debugEnemyMovementEnabled = enabled;
    events.emit("state:changed", undefined);
  }

  debugSetTowerAttacks(enabled: boolean): void {
    this.debugTowerAttacksEnabled = enabled;
    events.emit("state:changed", undefined);
  }

  debugSetSpeed(speed: number): void {
    this.speed = Math.max(0.25, Math.min(8, speed));
    events.emit("state:changed", undefined);
  }

  debugSpawnEnemy(definitionId: string, count = 1): number {
    let spawned = 0;
    for (let index = 0; index < Math.max(1, Math.min(50, Math.floor(count))); index += 1) {
      if (!this.spawn(definitionId)) break;
      this.waves.registerEnemy();
      spawned += 1;
    }
    events.emit("state:changed", undefined);
    return spawned;
  }

  debugDiagnosticSnapshot(): Record<string, unknown> {
    return {
      generatedAt: new Date().toISOString(),
      mapId: this.map.id,
      scenarioId: this.scenario?.id ?? null,
      difficulty: this.difficulty.id,
      lives: this.lives,
      balance: this.economy.balance,
      score: this.score,
      speed: this.speed,
      paused: this.paused,
      ended: this.ended,
      victory: this.victory,
      wave: this.waves.currentWaveNumber,
      totalWaves: this.waves.totalWaves,
      remainingEnemies: this.waves.remainingEnemies,
      activeEnemies: [...this.enemies.values()].map((enemy) => ({
        id: enemy.id,
        definitionId: enemy.definitionId,
        health: enemy.health,
        state: enemy.state,
      })),
      towers: [...this.towers.values()].map((tower) => ({
        id: tower.id,
        definitionId: tower.definitionId,
        level: tower.level,
        branch: tower.branch,
        targetingMode: tower.targetingMode,
      })),
      debug: {
        infiniteMoney: this.debugInfiniteMoneyEnabled,
        infiniteHealth: this.debugInfiniteHealthEnabled,
        enemyMovement: this.debugEnemyMovementEnabled,
        towerAttacks: this.debugTowerAttacksEnabled,
      },
    };
  }

  private reject(reason: string): false {
    events.emit("placement:feedback", { valid: false, message: reason });
    return false;
  }
}
